import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import {
	getBundlePlans,
	saveBundlePlan,
	getBundleSummary,
	getBundles,
	adjustBundle,
	getMonoPrice,
} from "../../actions/bundles";
import { money, count } from "../tenant/tenantLabels";
import { fieldClass, areaClass, labelClass, primaryButton, secondaryButton, smallButton } from "../template/templateLabels";
import { card } from "../ads/adLabels";
import TeamStories from "./TeamStories";

// Page bundles (the backend's routes/api/bundles.js): what was sold and printed from
// them, the plans on sale, and the bundles customers have. A bundle's money counts the
// day it is sold, so the kiosks' income leaves out what was printed from bundles.

const headRow =
	"text-xs font-semibold tracking-wide text-left text-gray-500 uppercase bg-gray-50 dark:text-gray-400 dark:bg-gray-900";
const cell = "px-4 py-3";
const num = "px-4 py-3 text-right tabular-nums";

// Today and the first of the month in Ulaanbaatar, as "YYYY-MM-DD"
const ubToday = () => new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
const monthStart = (day) => day.slice(0, 8) + "01";
const dayText = (date) => (date ? new Date(new Date(date).getTime() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10) : "—");
// A bundle keeps half pages: a colored page takes one and a half
const pagesText = (pages) => count(Math.floor(pages)) + (pages % 1 ? ",5" : "");

// What a plan saves on its pages printed mono one by one, in percent, or null when
// either is not known
const savingOf = (pages, price, monoPrice) =>
	monoPrice > 0 && pages > 0 && price > 0 ? Math.round((1 - price / (pages * monoPrice)) * 100) : null;

const EMPTY_PLAN = {
	name: "",
	pages: "",
	price: "",
	validityDays: "",
	shareable: false,
	active: false,
	sort: 0,
	description: "",
	photoCredits: 0,
};

// Lines that sell a plan, for the admin to start from (the site shows the plan's own)
const DESCRIPTION_IDEAS = [
	"Бие даалтаа хамтдаа хэвлээд, хамтдаа хамгаалаарай 💜",
	"Хичээл хийдэггүй баян найзаараа авхуулчих 😎",
	"5 найз, 1 багц, 1 дурсамж 📸",
];
const MAX_DESCRIPTION = 300;

const Stat = ({ title, value, note }) => (
	<div className={"p-5 " + card}>
		<p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
		<p className="mt-2 text-2xl font-semibold tracking-tight text-gray-800 truncate dark:text-gray-100">{value}</p>
		<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{note}</p>
	</div>
);

// What the customer pays for the plan beside what its pages cost one by one, as the
// admin types its pages and price
const PlanPrice = ({ pages, price, monoPrice }) => {
	if (!(pages > 0 && price > 0)) return null;
	const pagePrice = Math.round(price / pages);
	const saving = savingOf(pages, price, monoPrice);
	const tone =
		saving === null
			? "text-gray-700 bg-gray-50 dark:text-gray-200 dark:bg-gray-700"
			: saving > 0
			? "text-green-800 bg-green-50 dark:text-green-100 dark:bg-green-800"
			: "text-red-700 bg-red-50 dark:text-red-100 dark:bg-red-800";
	return (
		<div className={"p-3 rounded-lg text-sm sm:col-span-3 " + tone}>
			{saving !== null && (
				<p className="font-semibold">
					{saving > 0
						? `Ердийн үнээс ${saving}% хямд`
						: saving === 0
						? "Ердийн үнэтэй ижил"
						: `Ердийн үнээс ${-saving}% үнэтэй`}
					<span className="font-normal">
						{" "}
						· ердийн үнэ {money(pages * monoPrice)} ({count(pages)} × {money(monoPrice)})
					</span>
				</p>
			)}
			<p className={saving !== null ? "mt-1 text-xs" : "text-xs"}>
				Нэг хар хуудас {money(pagePrice)}, өнгөт {money(Math.round(pagePrice * 1.5))}.
				{saving === null && " Ердийн үнийг Тохиргоо → Үнэ дээр тогтоовол хямдрал харагдана."}
			</p>
		</div>
	);
};

const PlanForm = ({ plan, monoPrice, onSave, onCancel }) => {
	const [form, setForm] = useState(plan.id ? plan : EMPTY_PLAN);
	const [saving, setSaving] = useState(false);
	const set = (field) => (e) =>
		setForm({ ...form, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
	const submit = async (e) => {
		e.preventDefault();
		setSaving(true);
		const saved = await onSave(plan.id, {
			name: form.name,
			pages: Number(form.pages),
			price: Number(form.price),
			validityDays: Number(form.validityDays),
			shareable: Boolean(form.shareable),
			active: Boolean(form.active),
			sort: Number(form.sort) || 0,
			description: String(form.description || "").trim(),
			photoCredits: Number(form.photoCredits) || 0,
		});
		setSaving(false);
		if (saved) onCancel();
	};
	return (
		<form onSubmit={submit} className="grid gap-4 p-5 border-t border-gray-100 sm:grid-cols-3 dark:border-gray-700">
			<label className="block sm:col-span-3">
				<span className={labelClass}>Нэр</span>
				<input className={fieldClass} value={form.name} onChange={set("name")} placeholder="Бие даалт" required />
			</label>
			<label className="block">
				<span className={labelClass}>Хуудас (хар)</span>
				<input className={fieldClass} type="number" min="1" value={form.pages} onChange={set("pages")} required />
			</label>
			<label className="block">
				<span className={labelClass}>Үнэ ₮</span>
				<input className={fieldClass} type="number" min="1" value={form.price} onChange={set("price")} required />
			</label>
			<label className="block">
				<span className={labelClass}>Хүчинтэй хоног</span>
				<input className={fieldClass} type="number" min="1" value={form.validityDays} onChange={set("validityDays")} required />
			</label>
			<label className="block">
				<span className={labelClass}>Эрэмбэ</span>
				<input className={fieldClass} type="number" value={form.sort} onChange={set("sort")} />
			</label>
			<label className="block">
				<span className={labelClass}>Бэлэг зураг (10×15)</span>
				<input
					className={fieldClass}
					type="number"
					min="0"
					max="20"
					value={form.photoCredits}
					onChange={set("photoCredits")}
					disabled={!form.shareable}
				/>
			</label>
			<label className="block sm:col-span-3">
				<span className={labelClass}>Сурталчилгааны тайлбар</span>
				<textarea
					className={areaClass}
					rows="2"
					maxLength={MAX_DESCRIPTION}
					value={form.description || ""}
					onChange={set("description")}
					placeholder={DESCRIPTION_IDEAS[0]}
				/>
				<span className="flex flex-wrap gap-1 mt-2">
					{DESCRIPTION_IDEAS.map((idea) => (
						<button
							key={idea}
							type="button"
							className="px-2 py-1 text-xs text-purple-700 rounded-full bg-purple-50 hover:bg-purple-100 dark:bg-gray-700 dark:text-purple-300"
							onClick={() => setForm({ ...form, description: idea })}
						>
							{idea}
						</button>
					))}
				</span>
				<span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">
					Сайт дээр багцын картан дээр харагдана. {String(form.description || "").length}/{MAX_DESCRIPTION}
				</span>
			</label>
			<label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
				<input type="checkbox" checked={Boolean(form.shareable)} onChange={set("shareable")} />
				Багаараа (5 хүн хүртэл)
			</label>
			<label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
				<input type="checkbox" checked={Boolean(form.active)} onChange={set("active")} />
				Худалдаанд гаргах
			</label>
			<PlanPrice pages={Number(form.pages)} price={Number(form.price)} monoPrice={monoPrice} />
			<p className="text-xs text-gray-500 sm:col-span-3 dark:text-gray-400">
				Зарагдсан багцууд өөрийн нөхцлөөрөө үлдэнэ. Бэлэг зургийг багийн гишүүд багийн зургаа хэвлэхэд
				ашиглана: 5 бол хүн бүрт нэг.
			</p>
			<div className="flex gap-2 sm:col-span-3">
				<button type="submit" className={primaryButton} disabled={saving}>
					Хадгалах
				</button>
				<button type="button" className={secondaryButton} onClick={onCancel}>
					Болих
				</button>
			</div>
		</form>
	);
};

const Adjust = ({ bundle, onAdjust }) => {
	const [pages, setPages] = useState("");
	const [days, setDays] = useState("");
	const [saving, setSaving] = useState(false);
	const submit = async (e) => {
		e.preventDefault();
		setSaving(true);
		const done = await onAdjust(bundle, Number(pages) || 0, Number(days) || 0);
		setSaving(false);
		if (done) {
			setPages("");
			setDays("");
		}
	};
	return (
		<form onSubmit={submit} className="flex items-center gap-1">
			<input
				className={fieldClass + " w-20"}
				type="number"
				min="0"
				placeholder="+хуудас"
				value={pages}
				onChange={(e) => setPages(e.target.value)}
			/>
			<input
				className={fieldClass + " w-20"}
				type="number"
				min="0"
				placeholder="+хоног"
				value={days}
				onChange={(e) => setDays(e.target.value)}
			/>
			<button type="submit" className={smallButton} disabled={saving || (!pages && !days)}>
				Нэмэх
			</button>
		</form>
	);
};

const Bundles = ({ getBundlePlans, saveBundlePlan, getBundleSummary, getBundles, adjustBundle, getMonoPrice }) => {
	const [range, setRange] = useState(() => ({ start: monthStart(ubToday()), end: ubToday() }));
	const [summary, setSummary] = useState(null);
	const [plans, setPlans] = useState(null);
	const [editing, setEditing] = useState(null);
	const [email, setEmail] = useState("");
	const [bundles, setBundles] = useState(null);
	// What a mono page costs one by one, which a plan's saving is counted against
	const [monoPrice, setMonoPrice] = useState(null);

	const loadSummary = useCallback(async () => {
		setSummary(await getBundleSummary(range.start, range.end));
	}, [getBundleSummary, range]);
	const loadPlans = useCallback(async () => {
		setPlans(await getBundlePlans());
	}, [getBundlePlans]);
	const loadBundles = useCallback(
		async (search) => {
			setBundles(await getBundles(search));
		},
		[getBundles]
	);

	useEffect(() => {
		loadSummary();
	}, [loadSummary]);
	useEffect(() => {
		loadPlans();
		loadBundles("");
		getMonoPrice().then(setMonoPrice);
	}, [loadPlans, loadBundles, getMonoPrice]);

	const savePlan = async (id, body) => {
		const saved = await saveBundlePlan(id, body);
		if (saved) loadPlans();
		return saved;
	};

	const adjust = async (bundle, pages, days) => {
		const done = await adjustBundle(bundle.id, pages, days);
		if (done) {
			setBundles((list) =>
				list.map((one) =>
					one.id === bundle.id
						? { ...one, pagesLeft: done.pagesLeft, expiresAt: done.expiresAt, expired: false }
						: one
				)
			);
			loadSummary();
		}
		return done;
	};

	return (
		<Admin>
			<div className="flex flex-wrap items-end justify-between gap-4 my-6">
				<div>
					<h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-200">Багц</h2>
					<p className="text-sm text-gray-500 dark:text-gray-400">
						Багцын мөнгө зарагдсан өдрөө орлого болно. Киоскийн орлогод багцаар хэвлэсэн хэвлэлт орохгүй.
					</p>
				</div>
				<div className="flex items-end gap-2">
					<label className="block">
						<span className={labelClass}>Эхлэх</span>
						<input
							className={fieldClass}
							type="date"
							value={range.start}
							onChange={(e) => setRange({ ...range, start: e.target.value })}
						/>
					</label>
					<label className="block">
						<span className={labelClass}>Дуусах</span>
						<input
							className={fieldClass}
							type="date"
							value={range.end}
							onChange={(e) => setRange({ ...range, end: e.target.value })}
						/>
					</label>
				</div>
			</div>

			{!summary ? (
				<img src={spinner} alt="" className="w-10 mx-auto my-6" />
			) : (
				<>
					<div className="grid gap-4 mb-6 sm:grid-cols-2 xl:grid-cols-4">
						<Stat title="Борлуулалт" value={money(summary.soldAmount)} note={`${count(summary.soldCount)} багц`} />
						<Stat
							title="Багцаар хэвлэсэн"
							value={`${pagesText(summary.pagesPrinted)} хуудас`}
							note={`${count(summary.prints)} хэвлэлт, буцаалтын дараа`}
						/>
						<Stat
							title="Хэрэглэгчдэд үлдсэн"
							value={`${pagesText(summary.pagesOpen)} хуудас`}
							note={`${count(summary.openBundles)} идэвхтэй багц`}
						/>
						<Stat
							title="Хугацаа нь дууссан"
							value={`${pagesText(summary.pagesExpired)} хуудас`}
							note={`${count(summary.expiredBundles)} багцад ашиглагдаагүй үлдсэн`}
						/>
					</div>

					{summary.sold.length > 0 && (
						<section className={"mb-6 overflow-hidden " + card}>
							<div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
								<h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Өдрөөр</h3>
							</div>
							<div className="overflow-x-auto">
								<table className="w-full whitespace-no-wrap">
									<thead>
										<tr className={headRow}>
											<th className={cell}>Өдөр</th>
											<th className={cell}>Багц</th>
											<th className={num}>Тоо</th>
											<th className={num}>Дүн</th>
										</tr>
									</thead>
									<tbody className="text-sm text-gray-700 divide-y dark:divide-gray-700 dark:text-gray-300">
										{summary.sold.map((row) => (
											<tr key={row.day + row.name}>
												<td className={cell}>{row.day}</td>
												<td className={cell}>{row.name}</td>
												<td className={num}>{count(row.count)}</td>
												<td className={num}>{money(row.amount)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</section>
					)}
				</>
			)}

			<section className={"mb-6 overflow-hidden " + card}>
				<div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
					<div>
						<h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Багцын төрлүүд</h3>
						<p className="text-sm text-gray-500 dark:text-gray-400">
							Хар хуудас 1, өнгөт хуудас 1,5 хуудсаар тооцогдоно. Багийн багцад шинэ найз бүрт 5 хуудас бэлэг.
						</p>
					</div>
					{editing === null && (
						<button type="button" className={primaryButton} onClick={() => setEditing({})}>
							Багц нэмэх
						</button>
					)}
				</div>
				{editing && !editing.id && <PlanForm plan={editing} monoPrice={monoPrice} onSave={savePlan} onCancel={() => setEditing(null)} />}
				{!plans ? (
					<img src={spinner} alt="" className="w-10 mx-auto my-6" />
				) : plans.length === 0 ? (
					<p className="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">Багц алга. «Багц нэмэх»-ээр нэмнэ үү.</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full whitespace-no-wrap">
							<thead>
								<tr className={headRow}>
									<th className={cell}>Нэр</th>
									<th className={num}>Хуудас</th>
									<th className={num}>Үнэ</th>
									<th className={num}>1 хуудас</th>
									<th className={num}>Хямдрал</th>
									<th className={num}>Хоног</th>
									<th className={cell}>Төрөл</th>
									<th className={cell}>Төлөв</th>
									<th className={cell}></th>
								</tr>
							</thead>
							<tbody className="text-sm text-gray-700 divide-y dark:divide-gray-700 dark:text-gray-300">
								{plans.map((plan) =>
									editing && editing.id === plan.id ? (
										<tr key={plan.id}>
											<td colSpan="9" className="p-0">
												<PlanForm plan={plan} monoPrice={monoPrice} onSave={savePlan} onCancel={() => setEditing(null)} />
											</td>
										</tr>
									) : (
										<tr key={plan.id}>
											<td className={cell}>
												<p className="font-medium">{plan.name}</p>
												{plan.description && (
													<p className="max-w-xs text-xs text-gray-500 truncate dark:text-gray-400">{plan.description}</p>
												)}
											</td>
											<td className={num}>{count(plan.pages)}</td>
											<td className={num}>{money(plan.price)}</td>
											<td className={num}>{money(plan.pagePrice)}</td>
											<td className={num}>
												{savingOf(plan.pages, plan.price, monoPrice) === null
													? "—"
													: `${savingOf(plan.pages, plan.price, monoPrice)}%`}
											</td>
											<td className={num}>{plan.validityDays}</td>
											<td className={cell}>
												{plan.shareable ? "Багаараа" : "Ганцаараа"}
												{plan.photoCredits > 0 && (
													<p className="text-xs text-gray-500 dark:text-gray-400">{plan.photoCredits} зураг бэлэг</p>
												)}
											</td>
											<td className={cell}>
												{plan.active ? (
													<span className="px-2 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full dark:bg-green-700 dark:text-green-100">
														Худалдаанд
													</span>
												) : (
													<span className="px-2 py-1 text-xs font-semibold text-gray-600 bg-gray-100 rounded-full dark:bg-gray-700 dark:text-gray-200">
														Нуусан
													</span>
												)}
											</td>
											<td className={cell + " text-right"}>
												<button type="button" className={smallButton} onClick={() => setEditing(plan)}>
													Засах
												</button>
											</td>
										</tr>
									)
								)}
							</tbody>
						</table>
					</div>
				)}
			</section>

			<TeamStories />

			<section className={"mb-8 overflow-hidden " + card}>
				<div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
					<h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Хэрэглэгчдийн багц</h3>
					<form
						className="flex gap-2"
						onSubmit={(e) => {
							e.preventDefault();
							loadBundles(email.trim());
						}}
					>
						<input
							className={fieldClass}
							type="email"
							placeholder="Имэйлээр хайх"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
						<button type="submit" className={secondaryButton}>
							Хайх
						</button>
					</form>
				</div>
				{!bundles ? (
					<img src={spinner} alt="" className="w-10 mx-auto my-6" />
				) : bundles.length === 0 ? (
					<p className="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">Багц олдсонгүй.</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full whitespace-no-wrap">
							<thead>
								<tr className={headRow}>
									<th className={cell}>Эзэн</th>
									<th className={cell}>Багц</th>
									<th className={num}>Үлдсэн</th>
									<th className={cell}>Хүртэл</th>
									<th className={num}>Гишүүд</th>
									<th className={cell}>Нэмэх</th>
								</tr>
							</thead>
							<tbody className="text-sm text-gray-700 divide-y dark:divide-gray-700 dark:text-gray-300">
								{bundles.map((bundle) => (
									<tr key={bundle.id}>
										<td className={cell}>
											<p className="font-medium">{bundle.owner.name}</p>
											<p className="text-xs text-gray-500 dark:text-gray-400">{bundle.owner.email}</p>
										</td>
										<td className={cell}>
											{bundle.name}
											<p className="text-xs text-gray-500 dark:text-gray-400">
												{money(bundle.price)} · {dayText(bundle.paidAt)}
											</p>
										</td>
										<td className={num}>
											{pagesText(bundle.pagesLeft)} / {pagesText(bundle.pages)}
											{bundle.photoCredits > 0 && (
												<p className="text-xs text-gray-500 dark:text-gray-400">
													{bundle.photosLeft} / {bundle.photoCredits} зураг
												</p>
											)}
										</td>
										<td className={cell + (bundle.expired ? " text-red-600 dark:text-red-400" : "")}>
											{dayText(bundle.expiresAt)}
										</td>
										<td className={num}>{bundle.members}</td>
										<td className={cell}>
											<Adjust bundle={bundle} onAdjust={adjust} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</Admin>
	);
};

Bundles.propTypes = {
	getBundlePlans: PropTypes.func.isRequired,
	saveBundlePlan: PropTypes.func.isRequired,
	getBundleSummary: PropTypes.func.isRequired,
	getBundles: PropTypes.func.isRequired,
	adjustBundle: PropTypes.func.isRequired,
	getMonoPrice: PropTypes.func.isRequired,
};

export default connect(null, {
	getBundlePlans,
	saveBundlePlan,
	getBundleSummary,
	getBundles,
	adjustBundle,
	getMonoPrice,
})(Bundles);
